import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const schema = z.object({
  name: z.string().trim().max(200).optional(),
  email: z.string().trim().email().max(255).optional().or(z.literal("")),
  subject: z.string().trim().max(200).optional(),
  message: z.string().trim().min(1).max(4000),
});

export function ContactDialog({ trigger }: { trigger: ReactNode }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast.error(t("contact.error"));
      return;
    }
    setSending(true);
    const { error } = await supabase.from("contact_messages").insert({
      name: form.name.trim() || null,
      email: form.email.trim() || null,
      subject: form.subject.trim() || null,
      message: form.message.trim(),
    });
    setSending(false);
    if (error) {
      toast.error(t("contact.error"));
      return;
    }
    toast.success(t("contact.success"));
    setForm({ name: "", email: "", subject: "", message: "" });
    setOpen(false);
  }

  const inputCls =
    "w-full bg-paper ring-hairline px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-amber/50";
  const labelCls = "text-mono-caps text-[10px] text-ink/50 mb-1 block";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-paper max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-lg">{t("contact.title")}</DialogTitle>
          <DialogDescription className="text-sm text-ink/60">{t("contact.desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>
                {t("contact.name")} <span className="lowercase">({t("contact.optional")})</span>
              </label>
              <input value={form.name} onChange={set("name")} maxLength={200} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>
                {t("contact.email")} <span className="lowercase">({t("contact.optional")})</span>
              </label>
              <input type="email" value={form.email} onChange={set("email")} maxLength={255} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>
              {t("contact.subject")} <span className="lowercase">({t("contact.optional")})</span>
            </label>
            <input value={form.subject} onChange={set("subject")} maxLength={200} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>{t("contact.message")} *</label>
            <textarea
              required
              rows={5}
              maxLength={4000}
              value={form.message}
              onChange={set("message")}
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="w-full bg-ink text-paper text-sm font-medium px-3.5 py-2 hover:bg-amber transition-colors disabled:opacity-50"
          >
            {sending ? t("contact.sending") : t("contact.submit")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
